import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ExternalLink, GitBranch, Radar, ScrollText, Skull, X } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, ErrorState, LoadingState, PageHeader, SeverityBadge, describeError } from '@/components/primitives';
import WorldMap from '@/components/map/WorldMap';
import { useTimeRange } from '@/context/TimeRangeContext';
import { countryName, fmtCount } from '@/lib/geo';
import useResource from '@/pages/dashboard/useResource';
import { API, apiGet, authHeaders } from '@/pages/dashboard/api';
import { ago } from '@/pages/dashboard/format';
import '@/styles/darkweb.css';

/*
 * Dark web: confirmed findings about the organization (a triage table with a detail panel),
 * the ransomware picture for its sector and region, and exactly what is being watched.
 * Nothing raw reaches this page; an analyst confirms every finding first.
 */

const KIND = {
  ransomware_victim: { label: 'Ransomware leak site', icon: Skull },
  code_leak: { label: 'Public code', icon: GitBranch },
  paste: { label: 'Public paste', icon: ScrollText },
};
const MATCH_LABEL = { domain: 'Your domain', parent_domain: 'Parent domain', email_domain: 'Your email domain', name: 'Your name' };
const STATUSES = [
  { id: 'open', label: 'Open' },
  { id: 'acknowledged', label: 'Acknowledged' },
  { id: 'resolved', label: 'Resolved' },
  { id: 'false_positive', label: 'Not us' },
];
const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.id, s.label]));
const STATUS_TONE = { open: 'warning', acknowledged: 'info', resolved: 'success', false_positive: 'neutral' };
const SEV_RANK = { Critical: 0, High: 1, Medium: 2, Low: 3 };

function Stat({ label, value, sub }) {
  return (
    <div className="is-stats-item">
      <span className="is-stats-l">{label}</span>
      <b className="is-stats-v">{value}</b>
      {sub && <span className="is-stats-s">{sub}</span>}
    </div>
  );
}

function FindingPanel({ f, onClose, onChange }) {
  const [note, setNote] = useState(f.note || '');
  const [saving, setSaving] = useState(false);
  const kind = KIND[f.kind] || { label: 'Finding', icon: Radar };
  useEffect(() => setNote(f.note || ''), [f.id, f.note]);

  const save = async (status) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/exposure/findings/${f.id}`, { status, note }, { headers: authHeaders() });
      onChange(res.data);
      toast.success(status === f.status ? 'Note saved' : `Marked ${STATUS_LABEL[status].toLowerCase()}`);
    } catch (err) {
      toast.error("Couldn't update the finding", { description: describeError(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <aside className="dw-panel" aria-label="Finding detail" data-testid="finding-panel">
      <div className="dw-panel-h">
        <div className="dw-panel-tags">
          <SeverityBadge severity={f.severity} size="sm" />
          <Badge size="sm" icon={kind.icon}>{kind.label}</Badge>
          <Badge size="sm" tone={f.confidence === 'High' ? 'success' : 'neutral'}>{f.confidence} confidence</Badge>
        </div>
        <button type="button" className="dw-x" onClick={onClose} aria-label="Close finding"><X aria-hidden="true" /></button>
      </div>
      <div className="dw-panel-b">
        <h2 className="dw-panel-title">{f.title}</h2>
        {f.summary && <p className="dw-text">{f.summary}</p>}

        <section>
          <h3 className="dw-h3">Why it was matched to you</h3>
          <ul className="dw-matched">
            {f.matched_on.map((m) => (
              <li key={`${m.type}-${m.value}`}>
                <span className="dw-muted">{MATCH_LABEL[m.type] || 'Match'}</span>
                <code>{m.value}</code>
                {m.detail && <span className="dw-muted">{m.detail}</span>}
              </li>
            ))}
          </ul>
        </section>

        <dl className="dw-facts">
          {f.group && <div><dt>Group</dt><dd>{f.group}</dd></div>}
          {f.country && <div><dt>Country</dt><dd>{countryName(f.country)}</dd></div>}
          {f.sector && <div><dt>Sector</dt><dd>{f.sector}</dd></div>}
          <div><dt>Observed</dt><dd title={f.observed_at}>{f.observed_at ? ago(f.observed_at) : '-'}</dd></div>
          <div><dt>Confirmed</dt><dd title={f.confirmed_at}>{f.confirmed_at ? ago(f.confirmed_at) : '-'}</dd></div>
        </dl>

        {f.excerpt && (
          <figure className="dw-excerpt">
            <figcaption>Evidence, secrets masked</figcaption>
            <pre>{f.excerpt}</pre>
          </figure>
        )}
        {f.analyst_note && <p className="dw-analyst"><b>Analyst note.</b> {f.analyst_note}</p>}

        {f.actions?.length > 0 && (
          <section>
            <h3 className="dw-h3">What to do</h3>
            <ol className="dw-steps">{f.actions.map((a) => <li key={a}>{a}</li>)}</ol>
          </section>
        )}

        <section className="dw-decide">
          <h3 className="dw-h3">Status</h3>
          <div className="is-seg" role="group" aria-label="Status of this finding">
            {STATUSES.map((s) => (
              <button key={s.id} type="button" aria-pressed={f.status === s.id} disabled={saving} onClick={() => f.status !== s.id && save(s.id)}>{s.label}</button>
            ))}
          </div>
          <label className="dw-note">
            <span className="dw-muted">Note for your team</span>
            <textarea value={note} maxLength={500} rows={3} onChange={(e) => setNote(e.target.value)}
              onBlur={() => note !== (f.note || '') && save(f.status)} placeholder="What you checked, who owns it" />
          </label>
          {f.reference_url && (
            <a className="dw-source" href={f.reference_url} target="_blank" rel="noopener noreferrer">
              View the source <ExternalLink aria-hidden="true" /><span className="sr-only"> (opens in a new tab)</span>
            </a>
          )}
        </section>
      </div>
    </aside>
  );
}

function Findings({ findings, view, setView, selectedId, onSelect }) {
  const counts = useMemo(() => {
    const c = { all: findings.length };
    STATUSES.forEach((s) => { c[s.id] = findings.filter((f) => f.status === s.id).length; });
    return c;
  }, [findings]);
  const shown = useMemo(() => (view === 'all' ? findings : findings.filter((f) => f.status === view))
    .slice().sort((a, b) => (SEV_RANK[a.severity] ?? 9) - (SEV_RANK[b.severity] ?? 9) || String(b.observed_at).localeCompare(String(a.observed_at))), [findings, view]);

  return (
    <section className="dw-card" aria-label="Findings">
      <div className="dw-card-h">
        <h2 className="dw-card-title">Findings</h2>
        <div className="is-seg" role="group" aria-label="Filter by status">
          {[...STATUSES, { id: 'all', label: 'All' }].map((s) => (
            <button key={s.id} type="button" aria-pressed={view === s.id} onClick={() => setView(s.id)} data-testid={`dw-view-${s.id}`}>
              {s.label} <span className="dw-count">{counts[s.id] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>
      {findings.length === 0 ? (
        <div className="dw-empty" data-testid="dw-empty">
          <b>No confirmed findings about your organization</b>
          <span>Monitoring is running. When a match is confirmed by an analyst it appears here and you are notified.</span>
        </div>
      ) : shown.length === 0 ? (
        <div className="dw-empty"><b>Nothing {STATUS_LABEL[view] ? `marked ${STATUS_LABEL[view].toLowerCase()}` : 'here'}</b><span>Choose another status.</span></div>
      ) : (
        <div className="dw-table-wrap">
          <table className="dw-table">
            <thead>
              <tr><th scope="col">Severity</th><th scope="col">Finding</th><th scope="col">Matched on</th><th scope="col">Observed</th><th scope="col">Status</th></tr>
            </thead>
            <tbody>
              {shown.map((f) => {
                const kind = KIND[f.kind] || { label: 'Finding' };
                const m = f.matched_on[0];
                return (
                  <tr key={f.id} className={selectedId === f.id ? 'is-on' : undefined} onClick={() => onSelect(f.id)} data-testid={`finding-${f.id}`}>
                    <td><SeverityBadge severity={f.severity} size="sm" /></td>
                    <td className="dw-td-main">
                      <button type="button" className="dw-row-btn" onClick={(e) => { e.stopPropagation(); onSelect(f.id); }}>{f.title}</button>
                      <span className="dw-muted">{kind.label}{f.group ? ` · ${f.group}` : ''}</span>
                    </td>
                    <td>{m ? <><span className="dw-muted">{MATCH_LABEL[m.type] || 'Match'}</span> <code>{m.value}</code></> : '-'}</td>
                    <td className="dw-nowrap" title={f.observed_at}>{f.observed_at ? ago(f.observed_at) : '-'}</td>
                    <td><Badge size="sm" tone={STATUS_TONE[f.status]}>{STATUS_LABEL[f.status] || f.status}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SectorPicture({ days, rangeLabel }) {
  const fp = useResource(() => apiGet('/geo/footprint'), [], { poll: false });
  const rw = useResource(() => apiGet('/geo/ransomware', { days, sector: 'mine' }), [days]);
  const f = fp.data;
  const d = rw.data;
  if ((rw.error && !d) || (fp.error && !f)) return null;
  const area = f ? (f.basis === 'countries' ? f.countries : f.region_countries) : [];
  const where = f?.basis === 'countries' ? 'your countries' : f?.region ? `the ${f.region}` : null;
  const near = (d?.countries || []).filter((c) => area.includes(c.iso2));
  const nearTotal = near.reduce((a, c) => a + c.count, 0);
  const sector = d?.sector;
  const values = Object.fromEntries((d?.countries || []).map((c) => [c.iso2, c.count]));
  return (
    <section className="dw-card" aria-label="Ransomware in your sector">
      <div className="dw-card-h">
        <h2 className="dw-card-title">Ransomware in {sector ? `${sector}, your sector` : 'all sectors'}</h2>
        <Link to={`/map${sector ? '' : '?sector=all'}`} className="dw-link">Open the threat map</Link>
      </div>
      <div className="dw-sector">
        <Link to="/map" className="dw-sector-map" aria-label="Open the threat map">
          <WorldMap values={values} footprint={area} compact height={230}
            label={`Map of ransomware listings${sector ? ` in ${sector}` : ''}, ${rangeLabel.toLowerCase()}`} />
        </Link>
        <div className="dw-sector-facts">
          <div className="dw-sector-nums">
            <div><b>{d ? fmtCount(d.total) : '-'}</b><span>listed worldwide, {rangeLabel.toLowerCase()}</span></div>
            {where && <div><b>{d ? fmtCount(nearTotal) : '-'}</b><span>in {where}</span></div>}
          </div>
          <h3 className="dw-h3">Most active groups</h3>
          {d?.top_groups?.length ? (
            <ul className="dw-groups">
              {d.top_groups.slice(0, 5).map((g) => (
                <li key={g.group}>
                  <Link to={`/map?group=${encodeURIComponent(g.group)}`}>{g.group}</Link>
                  <span className="dw-bar" aria-hidden="true"><i style={{ width: `${(100 * g.count) / d.top_groups[0].count}%` }} /></span>
                  <b>{fmtCount(g.count)}</b>
                </li>
              ))}
            </ul>
          ) : <p className="dw-muted">{d ? 'No listings in this period.' : 'Loading…'}</p>}
          <p className="dw-fine">Public leak-site listings from ransomware.live. A listing is the group&apos;s claim, not a confirmed breach.</p>
        </div>
      </div>
    </section>
  );
}

function Coverage({ watch }) {
  const nothing = !watch.domains.length && !watch.names.length;
  return (
    <section className="dw-card" aria-label="What is watched for you">
      <div className="dw-card-h">
        <h2 className="dw-card-title">What we watch for you</h2>
        <Link to="/settings" className="dw-link">Edit in Settings</Link>
      </div>
      <div className="dw-cover">
        <div>
          <h3 className="dw-h3">Domains</h3>
          {watch.domains.length ? <div className="dw-chips">{watch.domains.map((x) => <code key={x} className="dw-chip">{x}</code>)}</div>
            : <p className="dw-muted" data-testid="no-domains-hint">None recorded. Without a domain, matching relies on your name, which is less exact.</p>}
        </div>
        <div>
          <h3 className="dw-h3">Names</h3>
          {watch.names.length ? <div className="dw-chips">{watch.names.map((x) => <span key={x} className="dw-chip">{x}</span>)}</div>
            : <p className="dw-muted">None recorded.</p>}
        </div>
        <div>
          <h3 className="dw-h3">Sources</h3>
          {watch.sources.length ? (
            <ul className="dw-sources">
              {watch.sources.map((s) => (
                <li key={s.name}><span>{s.name}</span><span className="dw-muted">{s.last_success_at ? `checked ${ago(s.last_success_at)}` : 'not checked yet'}</span></li>
              ))}
            </ul>
          ) : <p className="dw-muted">No source is active.</p>}
        </div>
      </div>
      {nothing && <p className="dw-warn">Nothing can be matched until a domain or name is recorded. <Link to="/settings">Add them in Settings</Link>.</p>}
    </section>
  );
}

export default function DarkWeb() {
  const { days, range } = useTimeRange();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('id');
  const view = params.get('view') || 'open';
  const list = useResource(() => apiGet('/exposure/findings', { status: 'all' }), []);
  const watch = useResource(() => apiGet('/exposure/watchlist'), []);
  const [overrides, setOverrides] = useState({});

  const set = useCallback((patch) => {
    setParams((p) => {
      const next = new URLSearchParams(p);
      Object.entries(patch).forEach(([k, v]) => (v == null ? next.delete(k) : next.set(k, v)));
      return next;
    }, { replace: true });
  }, [setParams]);

  const findings = useMemo(() => (list.data?.findings || []).map((f) => overrides[f.id] || f), [list.data, overrides]);
  const selected = findings.find((f) => f.id === selectedId) || null;
  const open = findings.filter((f) => f.status === 'open');
  const urgent = open.filter((f) => f.severity === 'Critical' || f.severity === 'High').length;
  const closed = findings.filter((f) => f.status === 'resolved' || f.status === 'false_positive').length;
  const sources = watch.data?.sources || [];
  const lastCheck = sources.map((s) => s.last_success_at).filter(Boolean).sort().pop();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && selectedId && !e.defaultPrevented) set({ id: null }); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, set]);

  return (
    <div className="pg dw" data-testid="exposure-page">
      <PageHeader
        title="Dark web"
        subtitle="Confirmed findings about your organization from ransomware leak sites, public code and pastes. An analyst reviews each one before it reaches you."
      />

      <section className="is-stats" aria-label="Key numbers">
        <Stat label="Open findings" value={list.data ? fmtCount(open.length) : '-'} sub={open.length ? 'Awaiting your decision' : 'Nothing waiting'} />
        <Stat label="Open, Critical or High" value={list.data ? fmtCount(urgent) : '-'} sub="Act on these first" />
        <Stat label="Closed" value={list.data ? fmtCount(closed) : '-'} sub="Resolved or not you" />
        <Stat label="Monitoring" value={sources.length ? `${sources.length} ${sources.length === 1 ? 'source' : 'sources'}` : '-'} sub={lastCheck ? `Last checked ${ago(lastCheck)}` : 'Not checked yet'} />
      </section>

      {list.loading && !list.data ? <LoadingState label="findings" rows={3} />
        : list.error && !list.data ? <ErrorState title="Couldn't load your findings" error={list.error} onRetry={list.reload} />
          : (
            <div className={`dw-triage${selected ? ' has-panel' : ''}`}>
              <Findings findings={findings} view={view} setView={(v) => set({ view: v === 'open' ? null : v })} selectedId={selectedId}
                onSelect={(id) => set({ id: id === selectedId ? null : id })} />
              {selected && <FindingPanel f={selected} onClose={() => set({ id: null })} onChange={(u) => setOverrides((o) => ({ ...o, [u.id]: u }))} />}
            </div>
          )}

      <SectorPicture days={days} rangeLabel={range.label} />
      {watch.data && <Coverage watch={watch.data} />}
    </div>
  );
}
